/**
 * Door check: decides what to tell the person at the door when a pass is scanned. Works offline against the project
 * in the browser: the pass signature is checked with the project key, then the guest is looked up. Pure.
 */
import type { Guest } from "./types";
import type { Project } from "./projectFile";
import { parsePassCode, verifyPassCode } from "./passCode";
import { canonTable } from "./seatingOps";

export type DoorResult =
  | { kind: "not-a-pass" }
  | { kind: "no-guests" }
  | { kind: "other-project" }
  | { kind: "altered" }
  | { kind: "unknown-guest" }
  | { kind: "valid"; guest: Guest; alreadyIn: boolean; declined: boolean };

export async function checkPass(code: string, project: Project): Promise<DoorResult> {
  if (!parsePassCode(code.trim())) return { kind: "not-a-pass" };
  if (project.data.guests.length === 0) return { kind: "no-guests" };
  const v = await verifyPassCode(code.trim(), project.passKey);
  if (!v.ok) return v.reason === "other-project" ? { kind: "other-project" } : { kind: "altered" };
  const guest = project.data.guests.find((g) => g.id === v.guestId);
  if (!guest) return { kind: "unknown-guest" };
  return { kind: "valid", guest, alreadyIn: guest.status === "attended", declined: guest.status === "declined" };
}

export const DOOR_MESSAGE: Record<Exclude<DoorResult["kind"], "valid" | "not-a-pass">, string> = {
  "no-guests": "This pass looks like a FreeDailyPro pass, but no project is open here. Open your project file to check it.",
  "other-project": "Not valid here: this pass belongs to a different project than the one that is open.",
  altered: "Not valid: this pass was not signed by the open project, so it may be a copy or altered.",
  "unknown-guest": "The signature is genuine, but this guest is no longer on the list.",
};

/** Where the guest sits, for the door desk: the table as written on the guest list ("3" reads as "Table 3"), or "" when none is set. */
export const doorTableLine = (g: { table?: string }): string => canonTable(g.table);
